<template>
  <div>
    <v-row>
      <!-- Exercise column -->
      <v-col cols="12">
        <exercise-logic-multiple
          exCollection="structureExCollection"
          refCollection="architectureExCollection"
          @ready="onReady"
          @error="onError"
          @saved="onSaved"
          @savedNext="onSavedNext"
          @loadedEx="onLoadedEx"
          @modifyEx="onModifyEx"
          @loadedRef="onLoadedRef"
          :doc="doc"
          :shared="false"
          :pid="true"
          :synced="false"
        >
          <template slot-scope="{ saveExercise, doc }">
            <v-row>
              <v-col cols="4" class="mb-4">
                <h2
                  class="headline"
                  v-text="$t('exercise_structure.step1.title')"
                ></h2>
              </v-col>
              <v-col cols="8" class="">
                <p
                  v-for="(item, index) in $t(
                    'exercise_structure.step1.description'
                  )"
                  :key="index"
                  v-text="item"
                />
              </v-col>
            </v-row>

            <v-navigation-drawer
              dark
              absolute
              expand-on-hover
              permanent
              right
              v-if="doc && refs"
              width="50%"
              height="100%"
            >
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-format-list-text</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <p>Content</p>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-account-multiple</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <p class="mb-5">Audiences</p>

                    <v-expansion-panels light :multiple="true">
                      <v-expansion-panel
                        v-for="(item, key, index) in refs.doc.audience"
                        :key="`${key}-${index}`"
                      >
                        <v-expansion-panel-header>
                          <p class="subtitle-2 black--text">
                            {{ audienceQuestions[index] }}
                          </p>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <div>
                            <draggable
                              class="choose"
                              :list="item"
                              group="notes"
                            >
                              <div
                                v-for="(el, index) in item"
                                :key="`${key}-inner-${index}`"
                                class="choose__item"
                              >
                                <p class="pa-1 ma-1">{{ el.title }}</p>
                                <div class="overlay">
                                  <v-btn
                                    @click="addToList(el, refs.doc.audience)"
                                    small
                                    class="white black--text"
                                    >Add to page</v-btn
                                  >
                                </div>
                              </div>
                            </draggable>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <p class="mb-5">Features, Benefits, Evidence</p>
                    <v-expansion-panels light :multiple="true">
                      <v-expansion-panel
                        v-for="(item, key, index) in refs.doc.content"
                        :key="`${key}-${index}`"
                      >
                        <v-expansion-panel-header>
                          <p class="subtitle-2 black--text">
                            {{ contentQuestions[index] }}
                          </p>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <div>
                            <draggable
                              class="choose"
                              :list="item"
                              group="notes"
                            >
                              <div
                                v-for="(el, index) in item"
                                :key="`${key}-inner-${index}`"
                                class="choose__item"
                              >
                                <p class="pa-1 ma-1">{{ el.title }}</p>
                                <div class="overlay">
                                  <v-btn
                                    @click="addToList(el, refs.doc.content)"
                                    small
                                    class="white black--text"
                                    >Add to page</v-btn
                                  >
                                </div>
                              </div>
                            </draggable>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-navigation-drawer>

            <v-row v-if="doc && refs">
              <v-col cols="10">
                <v-card>
                  <v-card-title>
                    Add a new page
                  </v-card-title>
                  <v-card-text>
                    <div class="d-flex align-end justify-space-between">
                      <v-text-field
                        v-model="listName"
                        label="Page title"
                        :hide-details="true"
                        class="ma-0"
                        color="amber"
                      ></v-text-field>

                      <v-btn
                        @click="addList"
                        color="amber"
                        class="ma-0 ml-5"
                        light
                        >Add Page</v-btn
                      >
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <h2 class="subtitle-2 mt-5 mt-5">Pages</h2>
                <div class="">
                  <draggable
                    class="wireframes"
                    group="outer"
                    :list="doc.listNames"
                    id="iso"
                  >
                    <structure-col
                      v-for="(item, index) in doc.listNames"
                      :key="`list-${index}`"
                      v-model="doc.list[item.name]"
                      :title="item.title"
                      :name="item.name"
                      class="iso-item wireframe"
                      :hideIcons="false"
                      :width="computedWidth"
                      @editList="onEditList"
                      @showNote="showNote"
                      @removeList="onRemoveList"
                    ></structure-col>
                  </draggable>
                </div>
              </v-col>
            </v-row>

            <bottom-navigation
              v-if="doc"
              :save="true"
              :next="false"
              :prev="false"
              :gridView="false"
              :gridToggle="false"
              @save="saveExercise(true)"
              @prev="prevStep"
              @next="saveExercise(false)"
            >
            </bottom-navigation>
          </template>
        </exercise-logic-multiple>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="800">
      <v-card v-if="dialogMode == 'move'">
        <v-card-title class="subtitle-2">Move to page</v-card-title>
        <v-card-text>
          <p>Select a page to move this to</p>
          <v-select
            label="Choose..."
            :items="this.doc.listNames"
            @change="selectList"
            item-text="title"
            item-name="name"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="amber" text @click="closeDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="dialogMode == 'save'">
        <v-card-title
          class="headline"
          v-text="$t('exercise_service.saved.title')"
        ></v-card-title>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="amber" text @click="closeDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="dialogMode == 'add'">
        <v-card-title>Add note</v-card-title>

        <v-card-text>
          <activation-add @complete="closeDialog" />
        </v-card-text>
      </v-card>

      <v-card light v-if="dialogMode == 'show'">
        <v-card-text class="pa-10">
          <activation-show :id="cardId" @complete="closeDialog" />
        </v-card-text>
      </v-card>

      <v-card v-if="dialogMode == 'edit'">
        <v-card-title class="headline">Edit note</v-card-title>

        <v-card-text>
          <activation-edit :id="cardId" @complete="closeDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState } from "vuex";
import { BottomNavigation } from "@/components/core";
import { ExerciseLogicMultiple } from "@/components/exercises";
import draggable from "vuedraggable";

import { StructureCol } from "@/views/user/exercises/architecture";
import { Isotope } from "@/vendor";

// const fb = require("@/firebaseConfig.js");

export default {
  name: "UserStructure",
  data() {
    return {
      e1: 1,
      exercises: null,
      refs: null,
      dialog: false,
      iso: null,
      audienceQuestions: [
        "What are the key messages you want audiences to take away?",
        "What are they trying to achieve?",
        "What do you want these audiences to do?",
        "What concerns or questions might they have?",
        "Anything else?",
      ],
      contentQuestions: [
        "How can you demonstrate what you do?",
        "How can you demonstrate the impact you will have?",
        "How can you show what it is like to work with you?",
        "What would communicate your difference?",
        "Anything else?",
      ],
      moveEl: null,
      moveList: null,
      dialogMode: "add",
      allItems: null,
      listName: "",
      results: {
        audience: [],
        features: [],
        benefits: [],
        reasons: [],
      },
      doc: {
        cols: 6,
        audience: {
          q1: [],
          q2: [],
          q3: [],
          q4: [],
          q5: [],
        },
        content: {
          q1: [],
          q2: [],
          q3: [],
          q4: [],
          q5: [],
        },
        list: {
          homepage: []
        },
        listNames: [
          {title: "Homepage", name: "homepage"}
        ],
        allItems: [],
        audienceAll: [],
        contentAll: [],
      },
      listLength: 1,
      debug: false,
    };
  },
  components: {
    BottomNavigation,
    ExerciseLogicMultiple,
    StructureCol,
    draggable,
  },
  computed: {
    ...mapState([
      "userProfile",
      "currentUser",
      "currentProject",
      "currentProjectModel",
    ]),
    computedWidth() {
      // if (this.doc) {
      //   let unit = 50 / 10;
      //   return { width: `${unit * this.doc.cols}%` };
      // } else {
      //   return null;
      // }
      return { width: `${50}%` };
    },
  },
  mounted() {},
  watch: {
    // doc: {
    //   deep: true,
    //    handler: function() {
    //     let items = [];
    //     Object.values(this.doc.list).forEach((x) => {
    //       x.forEach((y) => {
    //         console.log(y.title)
    //         items.push(y);
    //       });
    //     });
    //     this.usedList = items;
    //     console.log("doc changed");
    //   },
    // }
    // doc: {
    //   deep: true,
    //   handler: function(val) {
    //     if (val) {
    //       this.$nextTick(() => {
    //         this.initIso();
    //       });
    //     }
    //   },
    // },
    // refs: {
    //   deep: true,
    //   handler: function(val) {
    //     if (val) {
    //       this.$nextTick(() => {
    //         this.initIso();
    //       });
    //     }
    //   },
    // },
  },
  methods: {
    nextStep() {
      this.e1++;
    },
    prevStep() {
      this.e1--;
    },
    onReady() {},
    onError() {
      console.log("error");
    },
    onSaved() {
      console.log("saved");
    },
    onLoadedRef(refs) {
      if (refs) {
        this.refs = refs[0];
      }
    },
    onLoadedEx(items) {
      console.log("loaded exercise", items);
      if (items) {
        this.mergeArrays(items[0].doc);
        this.cols = items[0].doc.cols;
      }
    },
    onModifyEx(items) {
      if (items) {
        this.exercises = [...items];
      }
    },
    onSavedNext() {
      this.nextStep();
    },
    addIdea() {
      this.dialogMode = "add";
      this.dialog = true;
    },
    initIso() {
      this.iso = null;
      let isoEl = document.getElementById("iso");
      this.iso = new Isotope(isoEl, {
        itemSelector: ".iso-item",
        layoutMode: "packery",
        transitionDuration: 0,
        hiddenStyle: {
          opacity: 0,
        },
        visibleStyle: {
          opacity: 1,
        },
      });
    },
    layout() {
      console.log('layout')
      if (this.iso) {
        this.iso.layout();
      }
    },
    addList() {
      // let l = Object.keys(this.doc.list).length + 1;
      let lc = this.listName.replace(/\s/g, "").toLowerCase();
      this.doc.listNames.push({ title: this.listName, name: lc });
      this.listName = "";
      this.$set(this.doc.list, lc, []);
      this.layout();
    },
    addToList(el, list) {
      this.moveEl = el;
      this.moveList = list;
      this.dialogMode = "move";
      this.dialog = true;
    },
    selectList(ev) {
      let lc = ev.replace(/\s/g, "").toLowerCase();
      this.moveEl.type = "content";
      this.doc.list[lc].push(this.moveEl);
      // this.$set(this.doc.list[lc], [], this.doc.list[lc]);
      console.log(this.doc.list);
      this.dialog = false;
      this.dialogMode = this.moveList = this.moveList = null;
    },
    // inList(el) {
    //   if (this.doc) {
    //     return this.usedList.some((y) => y.title == el.title) ? true : false;
    //   } else {
    //     return false;
    //   }
    // },
    onRemoveList(name) {
      // loop through elements in list and re-distribute to master list
      let items = [];
      Object.values(this.doc.list[name]).forEach((x) => {
        items.push(x);
      });
      this.doc.allItems = [...this.doc.allItems, ...items];
      let item = this.doc.listNames.find((x) => x.name === name);
      let index = this.doc.listNames.indexOf(item);
      this.doc.listNames.splice(index, 1);
      this.$delete(this.doc.list, name);
    },
    onEditList(params) {
      console.log(params);
      let item = this.doc.listNames.find((x) => x.name === params.oldName);
      let index = this.doc.listNames.indexOf(item);
      console.log(index);
      let lc = params.newTitle.replace(/\s/g, "").toLowerCase();
      let renamedItem = {
        title: params.newTitle,
        name: lc,
      };
      this.$set(this.doc.listNames, index, renamedItem);

      let obj = this.doc.list[params.oldName];
      delete this.doc.list[params.oldName];
      this.$set(this.doc.list, lc, obj);
    },
    clone(obj) {
      Object.assign({}, obj);
    },
    renameKey(object, key, newKey) {
      const clonedObj = this.clone(object);
      const targetKey = clonedObj[key];
      delete clonedObj[key];
      clonedObj[newKey] = targetKey;
      return clonedObj;
    },
    mergeArrays(arr) {
      if (this.doc) {
        Object.entries(arr).forEach(([outerKey, value]) => {
          Object.entries(value).forEach(([key, value]) => {
            this.doc[outerKey][key] = value;
          });
        });
      } else {
        this.doc = arr;
      }
    },
    addNote() {
      this.dialogMode = "add";
      this.dialog = true;
    },
    editNote($event) {
      console.log("edit note", $event);
      this.dialogMode = "edit";
      this.cardId = $event;
      this.dialog = true;
    },
    showNote($event) {
      console.log("show note", $event);
      this.dialogMode = "show";
      this.cardId = $event;
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.dialogMode = "";
    },
    createList(ref) {
      let items = [];
      Object.values(ref.doc.audience).forEach((value) => {
        value.forEach((x) => {
          x.color = "blue lighten-4";
          items.push(x);
        });
      });
      Object.values(ref.doc.content).forEach((value) => {
        value.forEach((x) => {
          x.color = "lime accent-1";
          items.push(x);
        });
      });
      let itemsFromList = [];
      Object.values(ref.doc.list).forEach((value) => {
        value.forEach((x) => {
          itemsFromList.push(x);
        });
      });
      let all = [];
      items.forEach((x) => {
        if (!itemsFromList.some((y) => y.title == x.title)) {
          all.push(x);
        }
      });
      this.ref.allItems = all;
    },
  },
};
</script>

<style lang="scss">
.expansion-panel__container {
  // uncomment below
  background-color: rgba(255, 0, 0, 1) !important;
}
</style>
